import { useMutation, useQueryClient } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import toastr from 'react-hot-toast';
import { generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import React from 'react';
import { useWorkspaceStore } from 'stores';
import {
  ChatMessage,
  ChatMessageAttribute,
  ChatMessageResponse,
  FunctionCall,
  ApprovalOption,
  PluginsPillFunction,
} from 'types/Chats/ChatMessage';
import { v4 } from 'uuid';
type MutationVariables = Partial<ChatMessageAttribute> & { id: string };

export const useAddChatMessage = (
  chatID: string | undefined,
  type?: 'board' | 'chat' | 'memory',
  handleBoardEvent?: (events: string) => void
) => {
  const resource =
    type === 'board'
      ? SPARK_RESOURCE_TYPES.CHAT_MESSAGES_BOARD_STREAM
      : SPARK_RESOURCE_TYPES.CHAT_MESSAGES_STREAM;

  const [streamingMessage, setStreamingMessage] = React.useState<
    ChatMessage | undefined
  >();
  const newMessageIDRef = React.useRef<string>('');
  const boardFunctionCallIds = React.useRef<Set<string>>(new Set());
  const shouldCollapseBoardPill = React.useRef<boolean>(false);
  const queryClient = useQueryClient();
  const currentWorkspaceID = useWorkspaceStore(
    React.useCallback((state) => state.currentWorkspaceID, [])
  );

  const getChatMessagesQueryData = (): ChatMessageResponse | undefined => {
    // First check for standard query data
    const standardData = queryClient.getQueryData<ChatMessageResponse>([
      SPARK_RESOURCE_TYPES.CHAT_MESSAGES,
      chatID,
      'paginated',
      10,
    ]);

    if (standardData) return standardData;

    // Then check for infinite query data
    const infiniteData = queryClient.getQueryData<{
      pages: ChatMessageResponse[];
      pageParams: any[];
    }>([SPARK_RESOURCE_TYPES.CHAT_MESSAGES, chatID, 'paginated', 10]);

    // Return the first page if available
    return infiniteData?.pages[0];
  };

  const updateChatMessageInQueryData = (
    messageId: string,
    updateFn: (message: ChatMessage) => Partial<ChatMessage>
  ) => {
    // Update standard query data
    queryClient.setQueryData<ChatMessageResponse | undefined>(
      [SPARK_RESOURCE_TYPES.CHAT_MESSAGES, chatID],
      (old) => {
        if (!old?.data) return old;

        const newChatMessages = old.data.map((chatMessage) => {
          if (chatMessage.id === messageId) {
            return {
              ...chatMessage,
              ...updateFn(chatMessage),
            };
          }
          return chatMessage;
        });

        return {
          ...old,
          data: newChatMessages,
        };
      }
    );

    // Update infinite query data
    queryClient.setQueryData<
      { pages: ChatMessageResponse[]; pageParams: any[] } | undefined
    >([SPARK_RESOURCE_TYPES.CHAT_MESSAGES, chatID, 'paginated', 10], (old) => {
      if (!old?.pages || old.pages.length === 0) return old;

      // Update the first page with the new messages
      const updatedPages = old.pages.map((page, index) => {
        if (index === 0) {
          const updatedData = page.data.map((chatMessage) => {
            if (chatMessage.id === messageId) {
              return {
                ...chatMessage,
                ...updateFn(chatMessage),
              };
            }
            return chatMessage;
          });

          return {
            ...page,
            data: updatedData,
          };
        }
        return page;
      });

      return {
        ...old,
        pages: updatedPages,
      };
    });
  };

  const updateStreamingMessage = (
    updateFn: (message: ChatMessage) => Partial<ChatMessage>
  ) => {
    setStreamingMessage((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        ...updateFn(prev),
      };
    });
  };

  const handleDeltaEvent = (
    dataContent: string,
    fullMessage: string
  ): string => {
    try {
      // Ensure JSON is valid
      let jsonData = dataContent;
      if (!jsonData.endsWith('"}')) {
        jsonData = jsonData + '"}';
      }

      const parsed = JSON.parse(jsonData);
      if (parsed.v !== undefined) {
        // Add this delta to our message
        const deltaValue = parsed.v;

        // Collect all characters received so far
        const updatedFullMessage = fullMessage + deltaValue;

        // Collapse the plugins pill when delta streaming starts (plugins are done)
        const shouldCollapsePluginsPill = fullMessage === '';
        
        // Collapse board working pill when delta streaming starts after board functions
        const shouldCollapseBoardWorkingPill = shouldCollapseBoardPill.current;
        
        // Reset the flag after using it
        if (shouldCollapseBoardWorkingPill) {
          shouldCollapseBoardPill.current = false;
        }

        // Update UI with current message state
        updateStreamingMessage((prev) => ({
          attributes: {
            ...prev.attributes,
            content: updatedFullMessage,
            ...(shouldCollapsePluginsPill && prev.attributes.pluginsPill?.isVisible && {
              pluginsPill: {
                ...prev.attributes.pluginsPill,
                isVisible: false,
              },
            }),
            ...(shouldCollapseBoardWorkingPill && prev.attributes.boardWorkingPill?.isVisible && {
              boardWorkingPill: {
                isVisible: false,
              },
            }),
          },
        }));

        // Update React Query cache
        updateChatMessageInQueryData(
          newMessageIDRef.current,
          (chatMessage) => ({
            attributes: {
              ...chatMessage.attributes,
              content: updatedFullMessage,
              ...(shouldCollapsePluginsPill && chatMessage.attributes.pluginsPill?.isVisible && {
                pluginsPill: {
                  ...chatMessage.attributes.pluginsPill,
                  isVisible: false,
                },
              }),
              ...(shouldCollapseBoardWorkingPill && chatMessage.attributes.boardWorkingPill?.isVisible && {
                boardWorkingPill: {
                  isVisible: false,
                },
              }),
            },
          })
        );

        return updatedFullMessage;
      }
    } catch (err) {
      console.error('Error processing delta:', err);
    }
    return fullMessage;
  };

  const handleFunctionCallsEvent = (
    dataContent: string
  ): FunctionCall[] | undefined => {
    try {
      const functionCalls = JSON.parse(dataContent);
      if (functionCalls) {
        // Filter out board function calls from the results
        const filteredFunctionCalls = Array.isArray(functionCalls) 
          ? functionCalls.filter(call => !boardFunctionCallIds.current.has(call.callId))
          : functionCalls;

        updateStreamingMessage((prev) => ({
          attributes: {
            ...prev.attributes,
            functionCalls: filteredFunctionCalls,
          },
        }));

        updateChatMessageInQueryData(
          newMessageIDRef.current,
          (chatMessage) => ({
            attributes: {
              ...chatMessage.attributes,
              functionCalls: filteredFunctionCalls,
            },
          })
        );
        
        return filteredFunctionCalls;
      }
      return functionCalls;
    } catch (err) {
      console.error('Error processing functionCalls:', err);
      return undefined;
    }
  };

  const handleApprovalsEvent = (
    dataContent: string
  ): ApprovalOption[] | undefined => {
    try {
      const approvals = JSON.parse(dataContent);
      console.log('Processing approvals:', approvals);
      return approvals;
    } catch (err) {
      console.error('Error processing approvals:', err);
      return undefined;
    }
  };

  // New handlers for Plugins pill feature
  const handleFunctionCallEvent = (dataContent: string) => {
    try {
      const functionCallData = JSON.parse(dataContent);
      
      // Check if this is a board-related function call
      const boardFunctionKeys = [
        'sparkup_internal.read_board_node',
        'sparkup_internal.update_board_node',
        'sparkup_internal.create_board_node',
        'sparkup_internal.delete_board_node'
      ];
      
      const isBoardFunction = boardFunctionKeys.includes(functionCallData.key);
      
      if (isBoardFunction) {
        // Track this callId as a board function
        boardFunctionCallIds.current.add(functionCallData.callId);
        
        // Mark that board pill should be collapsed on next delta
        shouldCollapseBoardPill.current = true;
        
        // Show board working pill
        updateStreamingMessage((prev) => ({
          attributes: {
            ...prev.attributes,
            boardWorkingPill: {
              isVisible: true,
            },
          },
        }));

        updateChatMessageInQueryData(
          newMessageIDRef.current,
          (chatMessage) => ({
            attributes: {
              ...chatMessage.attributes,
              boardWorkingPill: {
                isVisible: true,
              },
            },
          })
        );
      } else {
        // Show regular plugins pill
        const newFunction: PluginsPillFunction = {
          callId: functionCallData.callId,
          pluginName: functionCallData.pluginName,
          functionName: functionCallData.functionName,
          iconURL: functionCallData.iconURL,
          status: 'pending',
        };

        updateStreamingMessage((prev) => ({
          attributes: {
            ...prev.attributes,
            pluginsPill: {
              functions: [
                ...(prev.attributes.pluginsPill?.functions || []),
                newFunction,
              ],
              isVisible: true,
            },
          },
        }));

        updateChatMessageInQueryData(
          newMessageIDRef.current,
          (chatMessage) => ({
            attributes: {
              ...chatMessage.attributes,
              pluginsPill: {
                functions: [
                  ...(chatMessage.attributes.pluginsPill?.functions || []),
                  newFunction,
                ],
                isVisible: true,
              },
            },
          })
        );
      }
    } catch (err) {
      console.error('Error processing function.call:', err);
    }
  };

  const handleFunctionArgumentsStartedEvent = (eventId: string) => {
    updateStreamingMessage((prev) => ({
      attributes: {
        ...prev.attributes,
        pluginsPill: {
          ...prev.attributes.pluginsPill,
          functions: prev.attributes.pluginsPill?.functions?.map((func) =>
            func.callId === eventId
              ? { ...func, status: 'arguments_started' }
              : func
          ) || [],
          isVisible: true,
        },
      },
    }));

    updateChatMessageInQueryData(
      newMessageIDRef.current,
      (chatMessage) => ({
        attributes: {
          ...chatMessage.attributes,
          pluginsPill: {
            ...chatMessage.attributes.pluginsPill,
            functions: chatMessage.attributes.pluginsPill?.functions?.map((func) =>
              func.callId === eventId
                ? { ...func, status: 'arguments_started' }
                : func
            ) || [],
            isVisible: true,
          },
        },
      })
    );
  };

  const handleFunctionArgumentsCompleteEvent = (eventId: string) => {
    updateStreamingMessage((prev) => ({
      attributes: {
        ...prev.attributes,
        pluginsPill: {
          ...prev.attributes.pluginsPill,
          functions: prev.attributes.pluginsPill?.functions?.map((func) =>
            func.callId === eventId
              ? { 
                  ...func, 
                  status: 'pending',
                  eventStatusTypeIconURL: undefined,
                  eventStatusTypeName: undefined,
                }
              : func
          ) || [],
          isVisible: true,
        },
      },
    }));

    updateChatMessageInQueryData(
      newMessageIDRef.current,
      (chatMessage) => ({
        attributes: {
          ...chatMessage.attributes,
          pluginsPill: {
            ...chatMessage.attributes.pluginsPill,
            functions: chatMessage.attributes.pluginsPill?.functions?.map((func) =>
              func.callId === eventId
                ? { 
                    ...func, 
                    status: 'pending',
                    eventStatusTypeIconURL: undefined,
                    eventStatusTypeName: undefined,
                  }
                : func
            ) || [],
            isVisible: true,
          },
        },
      })
    );
  };

  const handleFunctionResultsEvent = (dataContent: string) => {
    try {
      const results = JSON.parse(dataContent);
      if (Array.isArray(results)) {
        results.forEach((result) => {
          let status: 'pending' | 'arguments_started' | 'completed' | 'error';
          const eventStatusTypeName = result.pluginEvent?.eventStatusTypeName?.toLowerCase();
          
          // Map the event status to our internal status types
          switch (eventStatusTypeName) {
            case 'success':
              status = 'completed';
              break;
            case 'pending':
              status = 'pending';
              break;
            case 'error':
            case 'failed':
            case 'failure':
              status = 'error';
              break;
            default:
              // Default to error for unknown statuses
              status = 'error';
              break;
          }
          
          const statusIconURL = result.pluginEvent?.eventStatusTypeIconURL;
          // Treat empty string the same as null/undefined
          const validStatusIconURL = statusIconURL && statusIconURL.trim() !== '' ? statusIconURL : undefined;

          updateStreamingMessage((prev) => ({
            attributes: {
              ...prev.attributes,
              pluginsPill: {
                ...prev.attributes.pluginsPill,
                functions: prev.attributes.pluginsPill?.functions?.map((func) =>
                  func.callId === result.callId
                    ? { 
                        ...func, 
                        status: status,
                        eventStatusTypeIconURL: validStatusIconURL,
                        eventStatusTypeName: result.pluginEvent?.eventStatusTypeName,
                      }
                    : func
                ) || [],
                isVisible: true,
              },
            },
          }));

          updateChatMessageInQueryData(
            newMessageIDRef.current,
            (chatMessage) => ({
              attributes: {
                ...chatMessage.attributes,
                pluginsPill: {
                  ...chatMessage.attributes.pluginsPill,
                  functions: chatMessage.attributes.pluginsPill?.functions?.map((func) =>
                    func.callId === result.callId
                      ? { 
                          ...func, 
                          status: status,
                          eventStatusTypeIconURL: validStatusIconURL,
                          eventStatusTypeName: result.pluginEvent?.eventStatusTypeName,
                        }
                      : func
                  ) || [],
                  isVisible: true,
                },
              },
            })
          );
        });
      }
    } catch (err) {
      console.error('Error processing function.results:', err);
    }
  };

  const processEvent = (
    eventType: string,
    dataContent: string,
    fullMessage: string,
    inProgressDelta: boolean,
    eventId?: string
    // inProgressArguments: boolean
  ): {
    updatedFullMessage: string;
    functionCalls?: FunctionCall[];
    approvals?: ApprovalOption[];
  } => {
    let result = {
      updatedFullMessage: fullMessage,
      functionCalls: undefined as FunctionCall[] | undefined,
      approvals: undefined as ApprovalOption[] | undefined,
    };

    if (eventType === 'function.call') {
      handleFunctionCallEvent(dataContent);
    } else if (eventType === 'function.arguments.started' && eventId) {
      handleFunctionArgumentsStartedEvent(eventId);
    } else if (eventType === 'function.arguments.complete' && eventId) {
      handleFunctionArgumentsCompleteEvent(eventId);
    } else if (eventType === 'function.results') {
      handleFunctionResultsEvent(dataContent);
      result.functionCalls = handleFunctionCallsEvent(dataContent);
    } else if (eventType === 'approval:created') {
      result.approvals = handleApprovalsEvent(dataContent);
    } else if (eventType === 'board.events') {
      handleBoardEvent && handleBoardEvent(dataContent);
    } else if (inProgressDelta) {
      result.updatedFullMessage = handleDeltaEvent(dataContent, fullMessage);
    }
    // else if (inProgressArguments) {
    //   result.updatedFullMessage = handleFunctionArgumentsEvent(
    //     dataContent,
    //     fullMessage
    //   );
    // }

    return result;
  };

  const markStreamingComplete = () => {
    updateStreamingMessage((prev) => ({
      attributes: {
        ...prev.attributes,
        isStreaming: false,
      },
    }));

    updateChatMessageInQueryData(newMessageIDRef.current, (chatMessage) => ({
      attributes: {
        ...chatMessage.attributes,
        isStreaming: false,
      },
    }));
  };

  const markStreamingError = (errorMessage: string) => {
    updateStreamingMessage((prev) => ({
      attributes: {
        ...prev.attributes,
        isStreaming: false,
        error: errorMessage,
      },
    }));

    updateChatMessageInQueryData(newMessageIDRef.current, (chatMessage) => ({
      attributes: {
        ...chatMessage.attributes,
        isStreaming: false,
        error: errorMessage,
      },
    }));
  };

  const addChatMessage = async (variables: MutationVariables) => {
    const { content, boardID, items, memoryID } = variables || {};
    if (!currentWorkspaceID) {
      return Promise.reject('currentWorkspaceID not found');
    }

    if (type === 'chat' && !chatID) {
      return Promise.reject('chatID not found');
    }

    if (type === 'board' && !boardID) {
      return Promise.reject('boardID can not be null for board Chats');
    }

    const body = {
      data: {
        type: SPARK_RESOURCE_TYPES.CHAT_MESSAGES,
        attributes: {
          content: content,
          workspaceID: currentWorkspaceID,
          ...(type === 'board' ? { boardID } : { memoryID }),
          ...(items?.length && { items: JSON.stringify(items) }),
          ...(!!chatID && { chatID: chatID }),
        },
      },
    };
    return fetchWrapper<unknown, true>({
      url: generateURL(resource),
      method: 'POST',
      body,
      rawResponse: true,
    });
  };

  const mutation = useMutation('addChatMessage', addChatMessage, {
    onMutate: (variables) => {
      const newID = v4();
      const streamingMessageID = variables.id;
      newMessageIDRef.current = streamingMessageID;
      
      // Clear tracked board function callIds for new message
      boardFunctionCallIds.current.clear();
      
      // Reset board pill collapse flag for new message
      shouldCollapseBoardPill.current = false;

      const userPromptMessage: ChatMessage = {
        type: '',
        id: newID,
        attributes: {
          content: variables.content || '',
          authorRoleID: 1,
          createdAt: new Date().toISOString(),
          error: null,
        },
      };
      const streamingChatMessagesPlaceholder: ChatMessage = {
        type: '',
        id: newMessageIDRef.current,
        attributes: {
          content: '',
          functionCalls: [],
          createdAt: new Date().toISOString(),
          isStreaming: true,
          error: null,
        },
      };
      setStreamingMessage(streamingChatMessagesPlaceholder);

      // Update standard query data
      queryClient.setQueryData<ChatMessageResponse | undefined>(
        [SPARK_RESOURCE_TYPES.CHAT_MESSAGES, chatID],
        (old) => {
          return {
            ...old,
            data: [
              ...(old?.data || []),
              userPromptMessage,
              streamingChatMessagesPlaceholder,
            ],
          };
        }
      );

      // Update infinite query data
      queryClient.setQueryData<
        { pages: ChatMessageResponse[]; pageParams: any[] } | undefined
      >(
        [SPARK_RESOURCE_TYPES.CHAT_MESSAGES, chatID, 'paginated', 10],
        (old) => {
          if (!old?.pages || old.pages.length === 0) return old;

          // Create updated first page with new messages at the beginning
          const updatedFirstPage = {
            ...old.pages[0],
            data: [
              streamingChatMessagesPlaceholder,
              userPromptMessage,
              ...(old.pages[0].data || []),
            ],
          };

          return {
            ...old,
            pages: [updatedFirstPage, ...old.pages.slice(1)],
          };
        }
      );
    },
    onError: (error) => {
      console.log('onError', error);
      if (error instanceof Error) {
        toastr.error(`Failed to post Chat message: ${error.message}`);
      }
    },
    onSuccess: async (response, variables) => {
      if (!response.ok) {
        markStreamingError(response.statusText);
        return;
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No reader available');
      }

      // Variables to store the full message and current event state
      let fullMessage = '';
      let buffer = '';
      let inProgressDelta = false;
      // let inProgressArguments = false;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let functionCalls: FunctionCall[] | undefined = undefined;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let approvals: ApprovalOption[] | undefined = undefined;
      let currentEventType = '';
      let currentEventId = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // Mark streaming as complete
          markStreamingComplete();
          const currentChatMessages = getChatMessagesQueryData();
          if (!currentChatMessages) {
            queryClient.invalidateQueries([
              SPARK_RESOURCE_TYPES.BOARDS,
              variables.boardID,
            ]);
          }
          break;
        }

        // Use stream: true to handle multi-byte characters across chunks
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process all complete events in buffer
        while (true) {
          // Find complete events
          const eventMatch = buffer.match(/^event: ([^\n]+)\n/m);
          const dataMatch = buffer.match(/^data: ([^\n]+)\n/m);
          const idMatch = buffer.match(/^id: ([^\n]*)\n/m);

          if (!eventMatch && !dataMatch && !idMatch) {
            // No complete event found, wait for more data
            break;
          }

          if (idMatch && idMatch.index === 0) {
            // Process id line and capture the event ID
            currentEventId = idMatch[1].trim();
            console.log('Event ID received:', currentEventId);
            // Remove the processed id line from buffer
            buffer = buffer.substring(idMatch[0].length);
            continue;
          }

          if (eventMatch && eventMatch.index === 0) {
            // Process event line
            const eventName = eventMatch[1].trim();
            console.log('Event received:', eventName);
            currentEventType = eventName;
            inProgressDelta = eventName === 'delta';
            // inProgressArguments = eventName === 'function.arguments';
            // Remove the processed event line from buffer
            buffer = buffer.substring(eventMatch[0].length);
            continue;
          }

          if (dataMatch && dataMatch.index === 0) {
            // Process data line
            const dataContent = dataMatch[1].trim();
            console.log(
              'Data received for event:',
              currentEventType,
              dataContent
            );

            // Handle end of stream
            if (dataContent === '[END]') {
              break;
            }

            // Process the event and update state accordingly
            const result = processEvent(
              currentEventType,
              dataContent,
              fullMessage,
              inProgressDelta,
              currentEventId
              // inProgressArguments
            );

            fullMessage = result.updatedFullMessage;
            if (result.functionCalls) functionCalls = result.functionCalls;
            if (result.approvals) approvals = result.approvals;

            // Remove the processed data line from buffer
            buffer = buffer.substring(dataMatch[0].length);
            continue;
          }

          // If we get here, something's wrong with the pattern matching
          // Remove the first character and try again
          buffer = buffer.substring(1);
        }
      }
    },
  });
  return {
    ...mutation,
    streamingMessage,
  };
};
